import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  BoxV2,
  RadioButtonGroupMapping,
  RadioButtonsV2,
  AutoCompleteWithMultiSelect,
  LabraToolTipV2,
  NoteV2,
} from '@labrav/react-components'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { faGlobe } from '@fortawesome/pro-regular-svg-icons'
import { Typography } from '@mui/material'
import { FormikProps, useFormikContext } from 'formik'
import { useStyles } from '../FlyOutProductDimensionsPurchasingOptions/FlyOutProductDimensionsPurchasingOptions.styles'
import { fetchAwsCountriesList } from '../../../common/api/index'
import { FormLockedType } from '../../../types/FormLockedType'
import { offerAvailabilityOptions } from '../../../common/utils/productCreationUtil'
import { FlyoutProductSections } from '../../../types/addEditProduct'
import clsx from 'clsx'
import { useSelector } from '../../../store/hooks'
import type { FlyOutListingData } from '../../../types/flyout'
export type Country = {
  country: string
  code?: string
  allowed: boolean
}
export type AwsCountriesList = {
  countries: Country[]
}
export interface FlyOutProductOfferAvailabilityByCountryProps {
  requirementsLink: string
  detailView?: boolean
  detailViewTooltip?: string
}
export const FlyOutProductOfferAvailabilityByCountry: React.FC<
  FlyOutProductOfferAvailabilityByCountryProps & FormLockedType
> = ({ isFormLocked, detailView, detailViewTooltip }) => {
  const {
    values,
    errors,
    touched,
    setFieldValue,
    handleBlur,
    setFieldTouched,
  } = useFormikContext() as FormikProps<FlyoutProductSections>

  const { flyoutSellerInfo } = useSelector<{
    flyoutSellerInfo: FlyOutListingData
  }>(state => ({
    flyoutSellerInfo: state.flyoutSellerInfo,
  }))

  const isIndiaRegistered = flyoutSellerInfo?.countryRegistered === 'IN'

  const generalInformation = values.generalInformation
  const generalInformationErrors = errors.generalInformation
  const generalInformationTouched = touched.generalInformation

  const [awsCountriesLists, setAwsCountriesList] = useState<AwsCountriesList>({
    countries: [],
  })

  useEffect(() => {
    fetchAwsCountriesList()
      .then(response => {
        setAwsCountriesList(response.data)
      })
      .catch(error => {
        console.error('Failed to fetch countries list:', error)
      })
  }, [])

  const allCountryNames = awsCountriesLists?.countries.map(
    country => country.country
  )

  const { classes } = useStyles()

  const countryOptions: RadioButtonGroupMapping[] = offerAvailabilityOptions(
    isIndiaRegistered || isFormLocked || detailView
  )

  const resetCountrySelectorFields = async () => {
    const updatedGeneralInformation = {
      ...generalInformation,
      countriesSelected: [],
      excludedCountries: [],
      includedCountries: [],
    }
    await setFieldValue('generalInformation', updatedGeneralInformation)
    await setFieldTouched('generalInformation.countriesSelected', false)
  }

  const handleSelection = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    await resetCountrySelectorFields()
    await setFieldValue(
      'generalInformation.countryAvailability',
      (event.target as HTMLInputElement).value as string
    )
  }

  const handleCountrySelection = useCallback(
    async (event: React.ChangeEvent<object>, newValues: string[]) => {
      const updatedGeneralInformation = {
        ...generalInformation,
        countriesSelected: newValues,
        excludedCountries:
          generalInformation.countryAvailability === 'excludedCountries'
            ? newValues
            : [],
        includedCountries:
          generalInformation.countryAvailability === 'includedCountries'
            ? newValues
            : [],
      }
      await setFieldValue('generalInformation', updatedGeneralInformation)
    },
    [generalInformation]
  )

  const { selectTitle, selectDescrip } = useMemo(() => {
    if (generalInformation.countryAvailability === 'excludedCountries') {
      return {
        selectTitle: 'Excluded countries',
        selectDescrip:
          'Choose the countries for which you want to restrict offer availability.',
      }
    } else if (generalInformation.countryAvailability === 'includedCountries') {
      return {
        selectTitle: 'Allowlisted countries',
        selectDescrip:
          'Choose the countries in which you want to make your product available for purchase.',
      }
    } else {
      return { selectTitle: '', selectDescrip: '' }
    }
  }, [generalInformation.countryAvailability])

  const checkedValue = useMemo(() => {
    if (generalInformation.countryAvailability === 'excludedCountries') {
      return 'excludedCountries'
    } else if (generalInformation.countryAvailability === 'includedCountries') {
      return 'includedCountries'
    } else {
      return 'all'
    }
  }, [generalInformation.countryAvailability])

  return (
    <BoxV2
      heading={'Offer availability of your product by country'}
      headingIcon={
        <FontAwesomeIcon
          icon={faGlobe}
          style={{ width: '18px', height: '14px' }}
        />
      }
    >
      <div
        className={clsx(classes.productTypeContainer, classes.containerMargin)}
      >
        <Typography className={classes.sectionText}>
          Enable country-specific availability by identifying the countries you
          want to exclude or by limiting availability to allowlisted countries
          only.
        </Typography>
      </div>

      {isIndiaRegistered && (
        <NoteV2 description="As this account is based in India, you can only target buyers based in India. Buyers outside India won't be able to accept offers that you create." />
      )}

      <div className={classes.sectionContainer}>
        <div className={classes.textContainer}>
          <Typography className={classes.sectionHeading}>
            Select offer availability by country
          </Typography>
        </div>
        <LabraToolTipV2
          followCursor={detailView}
          tooltipPlacement="bottom-start"
          description={detailViewTooltip}
        >
          <RadioButtonsV2
            checkedValue={{
              key: checkedValue || '',
              value: checkedValue || '',
            }}
            name="generalInformation.countryAvailability"
            radioButtonMappings={countryOptions}
            handleChange={handleSelection}
            style={{ gap: '20px' }}
          />
        </LabraToolTipV2>
      </div>

      {generalInformation.countryAvailability &&
        generalInformation.countryAvailability !== 'all' && (
          <div className={clsx(classes.sectionContainer, classes.fullWidth)}>
            <div className={classes.textContainer}>
              <Typography className={classes.sectionHeading}>
                {selectTitle}
              </Typography>
              <Typography className={classes.sectionText}>
                {selectDescrip}
              </Typography>
            </div>
            <LabraToolTipV2
              followCursor={detailView}
              tooltipPlacement="bottom-start"
              description={detailViewTooltip}
              className={classes.fullWidth}
            >
              <div className={clsx(classes.fullWidth)}>
                <AutoCompleteWithMultiSelect
                  onChange={handleCountrySelection}
                  id={'generalInformation.countriesSelected'}
                  label={'Choose one or more countries'}
                  options={allCountryNames}
                  value={generalInformation.countriesSelected}
                  error={
                    !!(
                      generalInformationErrors?.countriesSelected &&
                      generalInformationTouched?.countriesSelected
                    )
                  }
                  helperText={
                    !!generalInformationTouched?.countriesSelected &&
                    generalInformationErrors?.countriesSelected &&
                    generalInformationErrors?.countriesSelected
                  }
                  onBlur={handleBlur}
                  disabled={isIndiaRegistered || isFormLocked || detailView}
                  viewOnly={detailView}
                />
              </div>
            </LabraToolTipV2>
          </div>
        )}
    </BoxV2>
  )
}
